import { AuthenticationEntity } from "@modules/auth/entities/auth.entity";
import { API_HEADER, API_URL } from "@modules/shared/enums/common-enum";
import { fetchWithAuth } from "@modules/shared/helpers/fetch-with-auth.helper";
import MediaService from "@modules/media/services/media.service";

export interface AuthArgs {
  auth: AuthenticationEntity | null;
  setAuth: (auth: AuthenticationEntity | null) => void;
  logout: () => void;
}

export interface TryItem {
  _id: string;
  query: string;
  title: string | null;
  image_url: string | null;
  position: number;
  active: boolean;
  market: string | null;
}

export type TryItemInput = Omit<TryItem, "_id">;

export type WeightMap = Record<string, Record<string, number>>;

export interface MarketRow {
  code: string;
  name: string;
  live: boolean;
  trainers: number;
  gyms: number;
  packages: number;
}

export interface MarketsView {
  /** false until an admin saves a selection: every market is offered. */
  configured: boolean;
  default_market: string;
  markets: MarketRow[];
}

export interface WeightsView {
  groups: string[];
  defaults: WeightMap;
  effective: WeightMap;
  updated_at: string | null;
  updated_by: string | null;
}

// Responses may or may not be wrapped in { data }.
const unwrap = <T>(res: any): T => (res && res.data !== undefined ? res.data : res) as T;

const jsonHeaders = () => ({ ...API_HEADER, "x-platform": "web" });

async function request<T>(
  args: AuthArgs,
  path: string,
  method: string,
  body?: unknown,
): Promise<T> {
  const res = await fetchWithAuth(
    `${API_URL.SEARCH_ADMIN}${path}`,
    {
      method,
      headers: jsonHeaders(),
      body: body === undefined ? undefined : JSON.stringify(body),
    },
    args.auth,
    args.setAuth,
    args.logout,
  );
  return unwrap<T>(res);
}

export const SearchConfigService = {
  listTryItems: (a: AuthArgs) => request<TryItem[]>(a, "/try-items", "GET"),
  createTryItem: (a: AuthArgs, input: TryItemInput) =>
    request<TryItem>(a, "/try-items", "POST", input),
  updateTryItem: (a: AuthArgs, id: string, input: TryItemInput) =>
    request<TryItem>(a, `/try-items/${id}`, "PUT", input),
  deleteTryItem: (a: AuthArgs, id: string) =>
    request<{ deleted: boolean }>(a, `/try-items/${id}`, "DELETE"),

  getMarkets: (a: AuthArgs) => request<MarketsView>(a, "/markets", "GET"),
  saveMarkets: (a: AuthArgs, live: string[]) =>
    request<MarketsView>(a, "/markets", "PUT", { live }),
  resetMarkets: (a: AuthArgs) => request<MarketsView>(a, "/markets", "DELETE"),

  getWeights: (a: AuthArgs) => request<WeightsView>(a, "/weights", "GET"),
  saveWeights: (a: AuthArgs, weights: WeightMap) =>
    request<WeightsView>(a, "/weights", "PUT", { weights }),
  resetWeights: (a: AuthArgs) =>
    request<WeightsView>(a, "/weights", "DELETE"),

  /** Uploads a photo and returns its public URL. */
  async uploadImage(a: AuthArgs, file: File): Promise<string> {
    const form = new FormData();
    form.append("file", file);
    const res: any = await new MediaService().uploadFile(
      form,
      a.auth,
      a.setAuth,
      a.logout,
    );
    const url = res?.data?.url ?? res?.url;
    if (!url) throw new Error("Upload did not return a URL");
    return url as string;
  },
};
